'use client';

import { useState } from 'react';
import { useTranslations } from '@/i18n/useTranslations';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Check, X } from 'lucide-react';
import { aiApi } from '@/lib/api/ai';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { toast } from 'sonner';
import type { ApprovalRequest } from '@comp-sys/shared';

interface AiApprovalCardProps {
  approval: ApprovalRequest;
  onApproved?: (stream: ReadableStream<Uint8Array>) => void;
}

export function AiApprovalCard({ approval, onApproved }: AiApprovalCardProps) {
  const t = useTranslations('ai');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const removeApproval = useAiChatStore((s) => s.removeApproval);
  const autoApprove = useAiChatStore((s) => s.autoApprove);

  const handleApprove = async () => {
    setLoading(true);
    try {
      // approve now returns an SSE stream (tool executes + agent loop continues)
      const stream = await aiApi.approve(approval.requestId, autoApprove);
      setStatus('approved');
      removeApproval(approval.requestId);
      toast.success(t('approvalApproved'));
      // Pass the SSE stream to the parent for processing
      if (onApproved) {
        onApproved(stream);
      }
    } catch {
      toast.error(t('approvalFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      await aiApi.reject(approval.requestId);
      setStatus('rejected');
      removeApproval(approval.requestId);
    } catch {
      toast.error(t('approvalFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center mb-3">
      <div className="max-w-[90%] w-full rounded-xl border border-warning/20 bg-warning-soft p-3">
        <div className="flex items-start gap-2 mb-2">
          <AlertTriangle className="h-4 w-4 text-warning-text mt-0.5 shrink-0" />
          <p className="text-sm text-foreground">{approval.description}</p>
        </div>
        {status === 'pending' ? (
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={handleReject} disabled={loading}>
              <X className="h-3.5 w-3.5 mr-1" />
              {t('reject')}
            </Button>
            <Button size="sm" onClick={handleApprove} disabled={loading}>
              <Check className="h-3.5 w-3.5 mr-1" />
              {t('confirmExecute')}
            </Button>
          </div>
        ) : (
          <div className={`text-xs font-medium ${status === 'approved' ? 'text-success-text' : 'text-error-text'}`}>
            {status === 'approved' ? t('executed') : t('rejected')}
          </div>
        )}
      </div>
    </div>
  );
}
